import { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Chip, TextField, InputAdornment, Grid, Accordion, AccordionSummary, AccordionDetails, CircularProgress, Alert, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus, Search, MessageCircle, ChevronDown, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { getTickets } from '../services/ticketService';
import type { Ticket as TicketType } from '../types/ticket';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1400px',
  margin: '0 auto',
  width: '100%'
}));

const HelpCenter = () => {
  const navigate = useNavigate();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);

  // Fetch tickets from API
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getTickets(page, 10);
        
        if (response.success && response.data) {
          setTickets(response.data.tickets);
          setTotalTickets(response.data.total);
        } else {
          throw new Error(response.message || 'Failed to fetch tickets');
        }
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tickets');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, [page]);

  const faqs = [
    {
      question: 'How do I create a new campaign?',
      answer: 'Click on the "+ Create Campaign" button in the sidebar. Follow the step-by-step wizard to set up your survey, configure rewards, and upload customer contacts.'
    },
    {
      question: 'What payment methods are supported for rewards?',
      answer: 'We support UPI payments for instant payouts, bank transfers, and digital gift cards. Rewards are processed automatically once a review is approved and posted.'
    },
    {
      question: 'How do I integrate with Google My Business?',
      answer: 'Go to Settings > Integrations, find Google My Business, and click "Connect". You\'ll be redirected to Google to authorize access. Once connected, reviews will be automatically synced.'
    },
    {
      question: 'Can I customize the survey questions?',
      answer: 'Yes! You can either use our AI-powered survey builder or create custom questions manually. Go to Create Campaign > Survey Builder to get started.'
    },
    {
      question: 'What happens if a customer gives a negative review?',
      answer: 'If a customer rates 3 stars or below, they won\'t be prompted to post publicly. You\'ll receive an alert and can respond privately to address their concerns.'
    },
    {
      question: 'How do I track my campaign performance?',
      answer: 'Visit the Dashboard to see real-time analytics including response rates, average ratings, reviews generated, and ROI metrics for each campaign.'
    },
    {
      question: 'Is there a limit on the number of surveys I can send?',
      answer: 'No, there\'s no limit! Your plan includes unlimited surveys. You only pay for successful reward payouts when reviews are posted.'
    },
    {
      question: 'How long does it take for rewards to be paid out?',
      answer: 'Rewards are typically processed within 24-48 hours after a review is verified and posted. Customers receive instant notifications once the payment is processed.'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return { bg: '#DBEAFE', color: '#1E40AF' };
      case 'in_progress': return { bg: '#FEF3C7', color: '#92400E' };
      case 'resolved': return { bg: '#D1FAE5', color: '#065F46' };
      case 'closed': return { bg: '#E5E7EB', color: '#374151' };
      default: return { bg: '#E5E7EB', color: '#374151' };
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return { bg: '#FEE2E2', color: '#991B1B' };
      case 'high': return { bg: '#FED7AA', color: '#9A3412' };
      case 'medium': return { bg: '#FEF3C7', color: '#92400E' };
      case 'low': return { bg: '#E5E7EB', color: '#374151' };
      default: return { bg: '#E5E7EB', color: '#374151' };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          ticket.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (ticket.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) || false);
    
    // Map 'resolved' filter to 'closed' status
    let matchesStatus = false;
    if (filterStatus === 'all') {
      matchesStatus = true;
    } else if (filterStatus === 'resolved') {
      matchesStatus = ticket.status === 'closed' || ticket.status === 'resolved';
    } else {
      matchesStatus = ticket.status === filterStatus;
    }
    
    return matchesSearch && matchesStatus;
  });

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: '1.875rem', md: '2.25rem' },
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                    color: '#111827',
                    mb: 0.5
                  }}
                >
                  Help Center
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1rem',
                    color: '#6B7280'
                  }}
                >
                  Get help and manage your support tickets
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Plus size={20} />}
                onClick={() => navigate('/help/new-ticket')}
                sx={{
                  bgcolor: '#4F46E5',
                  color: 'white',
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#4338CA',
                    boxShadow: 'none'
                  }
                }}
              >
                New Ticket
              </Button>
            </Stack>
          </Box>

          {/* Search and Filter */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: 'white',
                  '& fieldset': {
                    borderColor: '#E5E7EB'
                  },
                  '&:hover fieldset': {
                    borderColor: '#D1D5DB'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4F46E5'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} color="#9CA3AF" />
                  </InputAdornment>
                )
              }}
            />
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label="All"
                onClick={() => setFilterStatus('all')}
                sx={{
                  bgcolor: filterStatus === 'all' ? '#4F46E5' : 'white',
                  color: filterStatus === 'all' ? 'white' : '#6B7280',
                  border: filterStatus === 'all' ? 'none' : '1px solid #E5E7EB',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: filterStatus === 'all' ? '#4338CA' : '#F9FAFB'
                  }
                }}
              />
              <Chip
                label="Open"
                onClick={() => setFilterStatus('open')}
                sx={{
                  bgcolor: filterStatus === 'open' ? '#4F46E5' : 'white',
                  color: filterStatus === 'open' ? 'white' : '#6B7280',
                  border: filterStatus === 'open' ? 'none' : '1px solid #E5E7EB',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: filterStatus === 'open' ? '#4338CA' : '#F9FAFB'
                  }
                }}
              />
              <Chip
                label="In Progress"
                onClick={() => setFilterStatus('in_progress')}
                sx={{
                  bgcolor: filterStatus === 'in_progress' ? '#4F46E5' : 'white',
                  color: filterStatus === 'in_progress' ? 'white' : '#6B7280',
                  border: filterStatus === 'in_progress' ? 'none' : '1px solid #E5E7EB',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: filterStatus === 'in_progress' ? '#4338CA' : '#F9FAFB'
                  }
                }}
              />
              <Chip
                label="Resolved"
                onClick={() => setFilterStatus('resolved')}
                sx={{
                  bgcolor: filterStatus === 'resolved' ? '#4F46E5' : 'white',
                  color: filterStatus === 'resolved' ? 'white' : '#6B7280',
                  border: filterStatus === 'resolved' ? 'none' : '1px solid #E5E7EB',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: filterStatus === 'resolved' ? '#4338CA' : '#F9FAFB'
                  }
                }}
              />
            </Stack>
          </Stack>

          <Grid container spacing={3}>
            {/* Left Section - Support Tickets */}
            <Grid item xs={12} lg={8}>
              {/* Tickets Grid */}
              {filteredTickets.length === 0 ? (
                <Box
                  sx={{
                    bgcolor: 'white',
                    borderRadius: '12px',
                    p: 6,
                    textAlign: 'center',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '1rem' }}>
                    No tickets found
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {filteredTickets.map((ticket) => {
                    const statusColors = getStatusColor(ticket.status);
                    const priorityColors = getPriorityColor(ticket.priority);
                    
                    return (
                      <Grid item xs={12} key={ticket.id}>
                        <Box
                          onClick={() => navigate(`/help/ticket/${ticket.id}`)}
                          sx={{
                            bgcolor: 'white',
                            borderRadius: '12px',
                            p: 2.5,
                            border: '1px solid #E5E7EB',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                              borderColor: '#4F46E5',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                bgcolor: priorityColors.bg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              <MessageCircle size={20} color={priorityColors.color} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      color: '#4F46E5'
                                    }}
                                  >
                                    {ticket.ticket_number || ticket.id}
                                  </Typography>
                                  <Box
                                    sx={{
                                      width: 4,
                                      height: 4,
                                      borderRadius: '50%',
                                      bgcolor: '#D1D5DB'
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: '0.75rem',
                                      color: '#9CA3AF'
                                    }}
                                  >
                                    {ticket.category}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.75}>
                                  <Chip
                                    label={ticket.priority.toUpperCase()}
                                    size="small"
                                    sx={{
                                      bgcolor: priorityColors.bg,
                                      color: priorityColors.color,
                                      fontWeight: 600,
                                      fontSize: '0.65rem',
                                      height: 18,
                                      borderRadius: '4px',
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                  <Chip
                                    label={ticket.status.replace('_', ' ').toUpperCase()}
                                    size="small"
                                    sx={{
                                      bgcolor: statusColors.bg,
                                      color: statusColors.color,
                                      fontWeight: 600,
                                      fontSize: '0.65rem',
                                      height: 18,
                                      borderRadius: '4px',
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                </Stack>
                              </Stack>
                              <Typography
                                sx={{
                                  fontSize: '0.9375rem',
                                  fontWeight: 600,
                                  color: '#111827',
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {ticket.subject}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.8125rem',
                                  color: '#6B7280',
                                  mb: 1.25,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.5
                                }}
                              >
                                {ticket.description}
                              </Typography>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: '#9CA3AF'
                                  }}
                                >
                                  Updated {formatDate(ticket.updated_at)}
                                </Typography>
                                <Box
                                  sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    bgcolor: '#D1D5DB'
                                  }}
                                />
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <MessageCircle size={12} color="#9CA3AF" />
                                  <Typography
                                    sx={{
                                      fontSize: '0.75rem',
                                      color: '#9CA3AF'
                                    }}
                                  >
                                    {ticket.message_count || 0}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Grid>

            {/* Right Section - FAQ */}
            <Grid item xs={12} lg={4}>
              <Box
                sx={{
                  position: { lg: 'sticky' },
                  top: { lg: 100 },
                  bgcolor: 'white',
                  borderRadius: '12px',
                  p: 3,
                  border: '1px solid #E5E7EB'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <HelpCircle size={24} color="#4F46E5" />
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#111827'
                    }}
                  >
                    Quick Help
                  </Typography>
                </Stack>
                
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    mb: 3,
                    lineHeight: 1.6
                  }}
                >
                  Find answers to common questions before creating a ticket
                </Typography>

                <Stack spacing={1.5} sx={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto', pr: 1 }}>
                  {faqs.map((faq, index) => (
                    <Accordion
                      key={index}
                      elevation={0}
                      sx={{
                        bgcolor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px !important',
                        '&:before': {
                          display: 'none'
                        },
                        '&.Mui-expanded': {
                          margin: '0 !important',
                          mb: 1.5,
                          bgcolor: '#EEF2FF'
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ChevronDown size={18} color="#6B7280" />}
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            my: 1
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            color: '#111827',
                            lineHeight: 1.4
                          }}
                        >
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            color: '#6B7280',
                            lineHeight: 1.6
                          }}
                        >
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>

                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #E5E7EB' }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#111827',
                      mb: 1
                    }}
                  >
                    Still need help?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      color: '#6B7280',
                      mb: 2,
                      lineHeight: 1.6
                    }}
                  >
                    Our support team typically responds within 2-4 hours during business hours
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MessageCircle size={18} />}
                    onClick={() => navigate('/help/new-ticket')}
                    sx={{
                      textTransform: 'none',
                      color: '#4F46E5',
                      borderColor: '#4F46E5',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      py: 1,
                      '&:hover': {
                        bgcolor: '#EEF2FF',
                        borderColor: '#4338CA'
                      }
                    }}
                  >
                    Create New Ticket
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </ContentArea>
      </MainContent>
    </PageContainer>
  );
};

export default HelpCenter;

